import React, { useState } from "react";
import Layout, { Main } from "../Layout";
import { useLocation, Navigate } from "react-router-dom";
import { formatDate } from "../utils/DateFormater";
import { useApi } from "../Api/ApiContext";
import {
  Col,
  Form,
  FormGroup,
  Label,
  Row,
  Button,
  Alert,
  FormFeedback,
} from "reactstrap";
import Input from "../component/Input";
import useCelebritiesStore from "../store/CelebritiesStore";

const GENERIC_ERROR_MESSAGE = "Une erreur est survenue.";

const Order = () => {
  const { uploadVideoChunk, finalizeUploadChunk, getVideoProcessingStatus } =
    useApi();
  const location = useLocation();
  const order = location.state && location.state.order;
  const isVideo =
    order?.type === "dédicace vidéo" || order?.type === "vidéo business";
  const [file, setFile] = React.useState("");
  const [dedicaceFile, setDedicaceFile] = React.useState(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [success, setSuccess] = useState(false);

  const { celebrities } = useCelebritiesStore();
  const [durationError, setDurationError] = useState(null);

  const CHUNK_SIZE = 5 * 1024 * 1024;

  const handleChange = async (e) => {
    setProgress(0); // reinitialise la progressbar
    setFile(e.target.value);
    setDedicaceFile(e.target.files[0]);
    if (isVideo) {
      const dur = await getVideoDuration(e.target.files[0]);
      if (dur > 60) {
        setDedicaceFile(undefined);
        setDurationError("La durée de la vidéo ne doit pas excéder 1 minute.");
      } else {
        setDurationError(null);
      }
    }
  };

  const getVideoDuration = (file) => {
    return new Promise((resolve, reject) => {
      const video = document.createElement("video");

      video.onloadedmetadata = () => {
        resolve(video.duration);
      };

      video.onerror = () => {
        reject(new Error("Erreur vidéo"));
      };

      video.src = URL.createObjectURL(file);
    });
  };

  const uploadvideo = async () => {
    setLoading(true);
    setSuccess(false);
    setError(null);
    try {
      const uploadId = order.id;

      const totalChunks = Math.ceil(dedicaceFile.size / CHUNK_SIZE);

      for (let i = 0; i < totalChunks; i++) {
        const chunk = dedicaceFile.slice(
          i * CHUNK_SIZE,
          Math.min((i + 1) * CHUNK_SIZE, dedicaceFile.size)
        );

        const formData = new FormData();

        formData.append("chunk", chunk);
        formData.append("uploadId", uploadId);
        formData.append("chunkIndex", i);
        formData.append("totalChunks", totalChunks);
        formData.append("filename", dedicaceFile.name);

        await uploadVideoChunk(formData);

        setProgress(Math.round(((i + 1) / totalChunks) * 95));
      }

      setProgress(96);

      await finalizeUploadChunk({
        uploadId,
        filename: dedicaceFile.name,
        totalChunks,
      });

      const interval = setInterval(async () => {
        const status = await getVideoProcessingStatus(order.id);

        if (status.status === "COMPLETED") {
          clearInterval(interval);
          // afficher "Votre vidéo a été traitée"
          setProgress(100);
          setSuccess(true);
          setError(null);
          setLoading(false);
          setFile("");
          setDedicaceFile(undefined);
        }

        if (status.status === "FAILED") {
          clearInterval(interval);
          setError(status.error || GENERIC_ERROR_MESSAGE);
          setLoading(false);
          setProgress(0);
        }
      }, 3000);
    } catch (e) {
      setError(e?.response?.data?.error || GENERIC_ERROR_MESSAGE);
      setLoading(false);
      setProgress(0);
    }
  };

  if (!order) {
    return <Navigate to={"/admin/orders"} />;
  }

  if (
    order.deliveryStatus === "accepted" ||
    order.deliveryStatus === "delivered"
  ) {
    return (
      <Layout
        backPath={{ pathname: "/admin/orders" }}
        celebrities={celebrities.items}
      >
        <Main>
          <Row>
            <Col sm={8} lg={6} className="mx-auto">
              <Alert color={"info"}>
                Votre dédicace a déjà été validée ou livrée. Vous ne pouvez plus
                la modifier.
              </Alert>
            </Col>
          </Row>
        </Main>
      </Layout>
    );
  }
  return (
    <Layout
      backPath={{ pathname: "/admin/orders" }}
      celebrities={celebrities.items}
    >
      <Main className="my-3">
        {order.deliveryStatus === "rejected" && (
          <Row>
            <Col sm={8} lg={6} className="mx-auto">
              <Alert color={"warning"}>
                <div className={"mb-2"}>
                  La dédicace a été rejetée pour le motif ci-dessous.
                </div>
                <h6>Motif du rejet :</h6>
                <div className={"fw-semibold"}>{order.rejectedReason}</div>
                <div className={"mt-3"}>
                  Merci de réaliser une nouvelle dédicace pour votre fan.
                </div>
              </Alert>
            </Col>
          </Row>
        )}
        {order.deliveryStatus === "processed" && (
          <Row>
            <Col sm={8} lg={6} className="mx-auto">
              <Alert color={"info"}>
                <div className={"mb-2"}>
                  Vous avez déjà réalisé la dédicace pour cette commande.
                </div>
                <div className={"fw-semibold"}>
                  Vous pouvez modifier votre dédicace en réalisant une nouvelle
                  pour remplacer l'ancienne.
                </div>
              </Alert>
            </Col>
          </Row>
        )}
        {error && (
          <Row>
            <Col sm={8} lg={6} className="mx-auto">
              <Alert color={"danger"}>{error}</Alert>
            </Col>
          </Row>
        )}
        {success && (
          <Row>
            <Col sm={8} lg={6} className="mx-auto">
              <Alert color={"success"}>
                Votre fichier a bien été enregistré.
              </Alert>
            </Col>
          </Row>
        )}

        <Row>
          <Col sm={8} lg={6} className="mx-auto">
            <h2 className={"fs-5"}>
              Commande n° {order.id} du{" "}
              {formatDate(new Date(order.createdAt), "Y-MM-dd")}
            </h2>
          </Col>
        </Row>
        <Row>
          <Col sm={8} lg={6} className="mx-auto">
            <p>
              {order.dedicaceFrom} vous demande de faire une vidéo pour{" "}
              {order.dedicaceTo === order.dedicaceFrom
                ? "lui"
                : order.dedicaceTo}
              .
            </p>
          </Col>
        </Row>
        <Row>
          <Col sm={8} lg={6} className="mx-auto">
            <p>
              <span className={"fw-bold"}>Motif de la commande : </span>
              {order.orderReason}
            </p>
          </Col>
        </Row>
        <Row>
          <Col sm={8} lg={6} className="mx-auto">
            <p>
              <span className={"fw-bold"}>Description de la commande : </span>
              {order.message}
            </p>
          </Col>
        </Row>
        <Row>
          <Col sm={6} lg={4} className="offset-sm-2 offset-lg-3">
            <Form>
              <FormGroup>
                <Label className={"fw-bold"}>
                  Ajouter
                  {order.type === "dédicace musicale"
                    ? " le fichier audio"
                    : " la vidéo"}
                </Label>
                <Input
                  className={
                    durationError ? "is-invalid form-control" : "form-control"
                  }
                  type={"file"}
                  value={file}
                  onChange={handleChange}
                  accept={"video/*, audio/*"}
                  capture={isVideo}
                />
                <FormFeedback>{durationError}</FormFeedback>
              </FormGroup>

              {loading ? (
                <Row>
                  <Col lg={12} className="text-center text-white">
                    <progress
                      className="w-100"
                      id="file"
                      max="100"
                      value={progress}
                    >
                      {progress}%
                    </progress>
                    {progress}%
                  </Col>
                </Row>
              ) : (
                <Row>
                  <Col className={"offset-0 col-12 offset-sm-9 col-sm-3"}>
                    <Button
                      className={"w-100"}
                      color="primary"
                      onClick={uploadvideo}
                      disabled={!dedicaceFile}
                    >
                      Envoyer
                    </Button>
                  </Col>
                </Row>
              )}
            </Form>
          </Col>
        </Row>
      </Main>
    </Layout>
  );
};

export default Order;
